import React from 'react';
import { useMetaTags } from '../hooks/useMetaTags';
export function PrivacyPolicyPage() {
  useMetaTags({
    title: 'Privacy Policy | FindOnlineDegrees',
    description: 'Privacy policy for FindOnlineDegrees.org'
  });
  return <div className="min-h-screen bg-gray-100" data-id="element-1062">
      {/* Hero Section */}
      <section className="w-full bg-[#1a2332] text-white py-16 px-4" data-id="element-1063">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1064">
          <h1 className="text-3xl md:text-5xl font-bold mb-4" data-id="element-1065">
            Privacy Policy
          </h1>
          <p className="text-gray-300 text-sm" data-id="element-1066">Last updated: January 2026</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="max-w-4xl mx-auto px-4 py-12" data-id="element-1067">
        <div className="bg-white rounded-lg shadow-sm p-8 md:p-12 space-y-8" data-id="element-1068">
          <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1069">
            <p data-id="element-1070">
              This privacy notice for FindOnlineDegrees.org describes how and
              why we might collect, store, use, and/or share your information
              when you use our services, such as when you:
            </p>
            <ul className="list-disc pl-6 space-y-2" data-id="element-1071">
              <li data-id="element-1072">
                Visit our website, or any website of ours that links to this
                privacy notice
              </li>
              <li data-id="element-1073">
                Engage with us in other related ways, including any sales,
                marketing, or events
              </li>
            </ul>
            <p className="font-semibold" data-id="element-1074">
              Questions or concerns? Reading this privacy notice will help you
              understand your privacy rights and choices. If you do not agree
              with our policies and practices, please do not use our services.
            </p>
          </div>

          {/* Summary of Key Points */}
          <div data-id="element-1075">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1076">
              SUMMARY OF KEY POINTS
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1077">
              <ul className="list-disc pl-6 space-y-2" data-id="element-1078">
                <li data-id="element-1079">
                  <strong data-id="element-1080">What personal information do we process?</strong> When
                  you visit, use, or navigate our services, we may process
                  personal information depending on how you interact with us,
                  the choices you make, and the features you use.
                </li>
                <li data-id="element-1081">
                  <strong data-id="element-1082">Do we process sensitive personal information?</strong>{' '}
                  We do not process sensitive personal information.
                </li>
                <li data-id="element-1083">
                  <strong data-id="element-1084">Do we receive information from third parties?</strong>{' '}
                  We may receive technical and advertising data from third
                  parties, including advertising platforms and analytics
                  providers.
                </li>
                <li data-id="element-1085">
                  <strong data-id="element-1086">How do we process your information?</strong> To
                  provide, improve, and administer our services, communicate
                  with you, ensure security and fraud prevention, comply with
                  law, and with your consent where required.
                </li>
                <li data-id="element-1087">
                  <strong data-id="element-1088">
                    When and with whom do we share personal information?
                  </strong>{' '}
                  Only in specific circumstances (e.g., business transfers or
                  service providers).
                </li>
                <li data-id="element-1089">
                  <strong data-id="element-1090">How do we keep your information safe?</strong> With
                  organizational and technical measures, but no method is 100%
                  secure.
                </li>
                <li data-id="element-1091">
                  <strong data-id="element-1092">What are your rights?</strong> Depending on your
                  location, you may have rights under applicable privacy laws.
                </li>
                <li data-id="element-1093">
                  <strong data-id="element-1094">How do you exercise your rights?</strong> You can
                  exercise rights by contacting us (see Section 12).
                </li>
              </ul>
            </div>
          </div>

          {/* Table of Contents */}
          <div data-id="element-1095">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1096">
              TABLE OF CONTENTS
            </h2>
            <ol className="list-decimal pl-6 space-y-2 text-gray-700" data-id="element-1097">
              <li data-id="element-1098">WHAT INFORMATION DO WE COLLECT?</li>
              <li data-id="element-1099">HOW DO WE PROCESS YOUR INFORMATION?</li>
              <li data-id="element-1100">SMS / TEXT MESSAGE COMMUNICATIONS</li>
              <li data-id="element-1101">WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</li>
              <li data-id="element-1102">DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</li>
              <li data-id="element-1103">HOW LONG DO WE KEEP YOUR INFORMATION?</li>
              <li data-id="element-1104">HOW DO WE KEEP YOUR INFORMATION SAFE?</li>
              <li data-id="element-1105">DO WE COLLECT INFORMATION FROM MINORS?</li>
              <li data-id="element-1106">WHAT ARE YOUR PRIVACY RIGHTS?</li>
              <li data-id="element-1107">CONTROLS FOR DO-NOT-TRACK FEATURES</li>
              <li data-id="element-1108">DO WE MAKE UPDATES TO THIS NOTICE?</li>
              <li data-id="element-1109">HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</li>
            </ol>
          </div>

          {/* 1. What Information Do We Collect */}
          <div data-id="element-1110">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1111">
              1. WHAT INFORMATION DO WE COLLECT?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1112">
              <h3 className="font-bold text-lg" data-id="element-1113">
                Personal information you disclose to us
              </h3>
              <p data-id="element-1114">
                We collect personal information that you voluntarily provide to
                us when you express an interest in obtaining information about
                us or our services, when you participate in activities on the
                services, or otherwise when you contact us.
              </p>
              <p data-id="element-1115">
                <strong data-id="element-1116">Personal Information Provided by You.</strong> The
                personal information we collect may include:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1117">
                <li data-id="element-1118">Names</li>
                <li data-id="element-1119">Phone numbers</li>
                <li data-id="element-1120">Email addresses</li>
                <li data-id="element-1121">Mailing addresses</li>
                <li data-id="element-1122">Education level and interests</li>
                <li data-id="element-1123">Program preferences</li>
              </ul>

              <h3 className="font-bold text-lg mt-6" data-id="element-1124">
                Information automatically collected
              </h3>
              <p data-id="element-1125">
                We automatically collect certain information when you visit,
                use, or navigate the services. This information does not reveal
                your specific identity but may include device and usage
                information, such as:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1126">
                <li data-id="element-1127">IP address</li>
                <li data-id="element-1128">Browser and device characteristics</li>
                <li data-id="element-1129">Operating system</li>
                <li data-id="element-1130">Language preferences</li>
                <li data-id="element-1131">Referring URLs</li>
                <li data-id="element-1132">Device name and type</li>
                <li data-id="element-1133">Pages viewed and time spent</li>
                <li data-id="element-1134">Clickstream data</li>
              </ul>
            </div>
          </div>

          {/* 2. How Do We Process Your Information */}
          <div data-id="element-1135">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1136">
              2. HOW DO WE PROCESS YOUR INFORMATION?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1137">
              <p data-id="element-1138">
                We process your personal information for a variety of reasons,
                including:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1139">
                <li data-id="element-1140">To facilitate account creation and authentication</li>
                <li data-id="element-1141">To deliver and facilitate delivery of services to you</li>
                <li data-id="element-1142">To respond to user inquiries and offer support</li>
                <li data-id="element-1143">To send administrative information to you</li>
                <li data-id="element-1144">To fulfill and manage your requests</li>
                <li data-id="element-1145">To enable user-to-user communications</li>
                <li data-id="element-1146">
                  To request feedback and contact you about your use of our
                  services
                </li>
                <li data-id="element-1147">To send you marketing and promotional communications</li>
                <li data-id="element-1148">To deliver targeted advertising to you</li>
                <li data-id="element-1149">
                  To protect our services and investigate potential violations
                </li>
                <li data-id="element-1150">
                  To identify usage trends and determine the effectiveness of
                  campaigns
                </li>
                <li data-id="element-1151">To save or protect an individual's vital interest</li>
                <li data-id="element-1152">To comply with our legal obligations</li>
              </ul>

              <h3 className="font-bold text-lg mt-6" data-id="element-1153">Email Communications</h3>
              <p data-id="element-1154">
                When you submit a form on our website, you may consent to
                receive marketing and promotional emails from
                FindOnlineDegrees.org (operated by Media Matchers LLC) and its
                educational partners at the email address you provide. Marketing
                emails are sent from contact@findonlinedegrees.org. Every
                marketing email includes an unsubscribe link. You may opt out at
                any time and your request will be honored within 10 business
                days. Opting out of marketing email does not affect
                transactional or administrative messages related to a specific
                inquiry you have submitted.
              </p>
            </div>
          </div>

          {/* 3. SMS / Text Message Communications */}
          <div data-id="element-1155">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1156">
              3. SMS / TEXT MESSAGE COMMUNICATIONS
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1157">
              <h3 className="font-bold text-lg" data-id="element-1158">How We Collect SMS Consent</h3>
              <p data-id="element-1159">
                When you submit a form on our website, you may expressly consent
                to receive autodialed and/or pre-recorded marketing text
                messages (SMS/MMS) from FindOnlineDegrees.org (operated by Media
                Matchers LLC) and its educational partners at the mobile phone
                number you provide. This consent is obtained at the point of
                form submission and is documented with the date, time, IP
                address, and page URL at which consent was given.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1160">
                How We Use Your Phone Number for SMS
              </h3>
              <p data-id="element-1161">
                If you have provided consent, we may send you text messages for
                purposes including:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1162">
                <li data-id="element-1163">
                  Information about educational programs matching your interests
                </li>
                <li data-id="element-1164">Follow-up communications related to your inquiry</li>
                <li data-id="element-1165">
                  Promotional offers and marketing messages from us or our
                  educational partners
                </li>
              </ul>

              <h3 className="font-bold text-lg mt-6" data-id="element-1166">
                Consent Is Not Required
              </h3>
              <p data-id="element-1167">
                Your consent to receive SMS/MMS messages is never a condition of
                using our services or receiving information. You may choose not
                to provide consent and still access our website and resources.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1168">
                Message Frequency and Rates
              </h3>
              <p data-id="element-1169">
                Message frequency varies based on your interactions and
                preferences. Standard message and data rates may apply depending
                on your carrier and plan.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1170">How to Opt Out</h3>
              <p data-id="element-1171">
                You may revoke your consent and opt out of SMS communications at
                any time by:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1172">
                <li data-id="element-1173">Replying STOP to any text message you receive from us</li>
                <li data-id="element-1174">
                  Contacting us directly using the information in Section 12
                </li>
              </ul>
              <p data-id="element-1175">
                Opt-out requests will be honored promptly. After opting out, you
                may receive a single confirmation message. You will not receive
                further SMS marketing messages unless you provide new consent.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1176">For Assistance</h3>
              <p data-id="element-1177">
                Reply HELP to any text message for support information, or
                contact us directly using the information in Section 12.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1178">
                Third-Party SMS Senders
              </h3>
              <p data-id="element-1179">
                When you consent to receive communications from our educational
                partners, those partners may contact you directly via SMS under
                their own messaging programs and privacy policies. We encourage
                you to review the privacy policies of any educational
                institution or partner that contacts you.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1180">Record Retention</h3>
              <p data-id="element-1181">
                We retain records of SMS consent, including timestamp, IP
                address, form URL, and submitted form data, in accordance with
                our general data retention practices described in Section 6.
              </p>
            </div>
          </div>

          {/* 4. When and With Whom Do We Share */}
          <div data-id="element-1182">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1183">
              4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1184">
              <p data-id="element-1185">We may share information in the following situations:</p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1186">
                <li data-id="element-1187">
                  <strong data-id="element-1188">Business Transfers:</strong> In connection with, or
                  during negotiations of, any merger, sale of company assets,
                  financing, or acquisition.
                </li>
                <li data-id="element-1189">
                  <strong data-id="element-1190">Educational Institutions:</strong> We share your
                  information with schools and educational partners that match
                  your interests.
                </li>
                <li data-id="element-1191">
                  <strong data-id="element-1192">Service Providers:</strong> With third-party vendors,
                  service providers, contractors, or agents who perform services
                  for us.
                </li>
                <li data-id="element-1193">
                  <strong data-id="element-1194">Advertising Partners:</strong> With advertising
                  partners to deliver targeted ads.
                </li>
                <li data-id="element-1195">
                  <strong data-id="element-1196">Legal Requirements:</strong> When required by law or
                  to protect rights, safety, and property.
                </li>
              </ul>
            </div>
          </div>

          {/* 5. Cookies and Tracking */}
          <div data-id="element-1197">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1198">
              5. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1199">
              <p data-id="element-1200">
                Yes, we use cookies and similar tracking technologies to access
                or store information. See our Cookie Notice for more details
                about how we use cookies.
              </p>
            </div>
          </div>

          {/* 6. How Long Do We Keep */}
          <div data-id="element-1201">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1202">
              6. HOW LONG DO WE KEEP YOUR INFORMATION?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1203">
              <p data-id="element-1204">
                We keep your information for as long as necessary to fulfill the
                purposes outlined in this privacy notice unless otherwise
                required by law. When we have no ongoing legitimate business
                need to process your personal information, we will delete or
                anonymize it.
              </p>
            </div>
          </div>

          {/* 7. How Do We Keep Information Safe */}
          <div data-id="element-1205">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1206">
              7. HOW DO WE KEEP YOUR INFORMATION SAFE?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1207">
              <p data-id="element-1208">
                We have implemented appropriate technical and organizational
                security measures designed to protect the security of any
                personal information we process. However, no electronic
                transmission over the internet or information storage technology
                can be guaranteed to be 100% secure.
              </p>
            </div>
          </div>

          {/* 8. Minors */}
          <div data-id="element-1209">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1210">
              8. DO WE COLLECT INFORMATION FROM MINORS?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1211">
              <p data-id="element-1212">
                We do not knowingly solicit data from or market to children
                under 18 years of age. If you become aware that a child has
                provided us with personal information, please contact us. If we
                learn we have collected personal information from a child under
                18, we will delete that information.
              </p>
            </div>
          </div>

          {/* 9. Privacy Rights */}
          <div data-id="element-1213">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1214">
              9. WHAT ARE YOUR PRIVACY RIGHTS?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1215">
              <p data-id="element-1216">
                Depending on your location, you may have the following rights:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1217">
                <li data-id="element-1218">The right to access your personal information</li>
                <li data-id="element-1219">
                  The right to request correction of your personal information
                </li>
                <li data-id="element-1220">
                  The right to request deletion of your personal information
                </li>
                <li data-id="element-1221">The right to opt out of marketing communications</li>
                <li data-id="element-1222">
                  The right to opt out of the sale or sharing of personal
                  information (where applicable)
                </li>
              </ul>
              <p className="mt-4" data-id="element-1223">
                To exercise these rights, please contact us using the
                information in Section 12.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1224">
                Opt Out of Marketing Email
              </h3>
              <p data-id="element-1225">
                To opt out of marketing email: Click the unsubscribe link in any
                marketing email you receive from us, or contact us at the
                address in Section 12. Requests are processed within 10 business
                days.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1226">
                California Residents — Opt Out of Sale/Sharing
              </h3>
              <p data-id="element-1227">
                California residents — opt out of sale/sharing of personal
                information: To exercise your right to opt out under CCPA/CPRA,
                contact us at the address in Section 12 with the subject line
                "California Privacy Opt-Out." We will process your request
                within 15 business days.
              </p>
            </div>
          </div>

          {/* 10. Do Not Track */}
          <div data-id="element-1228">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1229">
              10. CONTROLS FOR DO-NOT-TRACK FEATURES
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1230">
              <p data-id="element-1231">
                Most web browsers and some mobile operating systems include a
                Do-Not-Track ("DNT") feature or setting you can activate to
                signal your privacy preference. At this stage, no uniform
                technology standard for recognizing and implementing DNT signals
                has been finalized.
              </p>
            </div>
          </div>

          {/* 11. Updates */}
          <div data-id="element-1232">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1233">
              11. DO WE MAKE UPDATES TO THIS NOTICE?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1234">
              <p data-id="element-1235">
                Yes, we will update this notice as necessary to stay compliant
                with relevant laws. The updated version will be indicated by an
                updated "Last updated" date at the top of this privacy notice.
              </p>
            </div>
          </div>

          {/* 12. Contact Us */}
          <div data-id="element-1236">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1237">
              12. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1238">
              <p data-id="element-1239">
                If you have questions or comments about this notice, please
                contact us at:
              </p>
              <p className="font-semibold" data-id="element-1240">Media Matchers LLC</p>
              <p data-id="element-1241">
                1865 Chestnut Street
                <br data-id="element-1242" />
                San Francisco, CA 94123
              </p>
              <p data-id="element-1243">
                Email:{' '}
                <a href="mailto:contact@findonlinedegrees.org" className="text-[#3b82f6] underline hover:text-blue-800" data-id="element-1244">
                  contact@findonlinedegrees.org
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>;
}